import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, type LayoutChangeEvent } from 'react-native';
import { LineChart, type IconType, theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { ErrorBox } from '~/components/ErrorBox';
import { formatTickLabel, thinTickLabels } from '~/components/behaviorChartTickLabels';

/** Height (px) of the controls slot. Exported so consumers can size their Select / Tooltip
 *  to match the controls region the component reserves at the top of the chart. */
export const CONTROLS_HEIGHT = 40;

/** Single source of truth for the chart's three series — the LineChart consumes
 *  `SERIES_COLORS` / `SERIES_ICONS` (derived below) and renders its own legend
 *  (labels come from the series data). Adding a series here flows everywhere. */
export const BEHAVIOR_SERIES = [
  { key: 'praises', label: 'Praises', icon: 'star', color: theme.colors.lightPurple },
  { key: 'warnings', label: 'Warnings', icon: 'hexagon', color: theme.colors.yellow },
  { key: 'absent', label: 'Absent', icon: 'square', color: theme.colors.navy },
] as const satisfies ReadonlyArray<{ key: string; label: string; icon: IconType; color: string }>;

const SERIES_COLORS = Object.fromEntries(BEHAVIOR_SERIES.map((i) => [i.key, i.color])) as Record<
  (typeof BEHAVIOR_SERIES)[number]['key'],
  string
>;

const SERIES_ICONS = Object.fromEntries(BEHAVIOR_SERIES.map((i) => [i.key, i.icon])) as Record<
  (typeof BEHAVIOR_SERIES)[number]['key'],
  IconType
>;

type LineChartData = React.ComponentProps<typeof LineChart>['data'];

type Props = {
  data?: LineChartData | null;
  loading?: boolean;
  hasError?: boolean;
  errorMessage: string;
  title: string;
  /** When true, render the title in the chart's header row (next to the legend).
   *  When false (default), the title is used only as the chart's a11y label. */
  showTitle?: boolean;
  /** Layout for the controls slot:
   *  - "overlay" (default): absolute top-left, overlaying the chart. Right for tight containers
   *    where every pixel of plot height matters (e.g., teacher OverviewTab).
   *  - "stacked": rendered in a flex row ABOVE the chart. Right for full-page admin layouts where
   *    dedicated header space reads cleaner. */
  controlsLayout?: 'overlay' | 'stacked';
  /** Controls slot (typically a Select). */
  controls: ReactNode;
  /** When true (default), absences render as scatter dots pinned to the bottom axis instead of a
   *  count-proportional line. Right for per-student views where absences are sparse 0/1 signals;
   *  set false for school-wide aggregates where absent counts are meaningful as a trend. */
  absentAsBaseline?: boolean;
};

export function BehaviorChartView({
  data,
  loading = false,
  hasError = false,
  errorMessage,
  title,
  showTitle = false,
  controlsLayout = 'overlay',
  controls,
  absentAsBaseline = true,
}: Props) {
  const seriesTypes = {
    praises: 'line' as const,
    warnings: 'line' as const,
    absent: absentAsBaseline ? ('scatter' as const) : ('line' as const),
  };
  const baselineOnly = absentAsBaseline ? { absent: true } : undefined;
  const [chartContainerHeight, setChartContainerHeight] = useState(0);

  const handleChartContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setChartContainerHeight(e.nativeEvent.layout.height);
  }, []);

  const isStacked = controlsLayout === 'stacked';

  // The query hooks hand back ticks as raw ISO dates ("2026-05-19"). Format and
  // thin them here so the data-fetching layer doesn't have to know the
  // LineChart-internal step heuristic the thinning mirrors.
  const chartData = useMemo<LineChartData | null | undefined>(() => {
    if (!data) return data;
    return {
      ...data,
      ticks: thinTickLabels(data.ticks.map(formatTickLabel)),
      tickDisplayLabels: data.ticks,
    };
  }, [data]);

  return (
    <View style={styles.root}>
      {isStacked && (
        <View style={styles.controlsRow}>
          <View style={styles.controlsLeft}>{controls}</View>
        </View>
      )}
      <View style={styles.chartContainer} onLayout={handleChartContainerLayout}>
        {!isStacked && <View style={styles.chartControlsOverlay}>{controls}</View>}
        {loading && <Loading backgroundColor="transparent" />}
        {!loading && hasError && (
          <View style={styles.errorContainer}>
            <ErrorBox text={errorMessage} containerStyle={styles.errorBox} />
          </View>
        )}
        {!loading && !hasError && chartData && chartContainerHeight > 0 && (
          <LineChart
            data={chartData}
            title={title}
            showTitle={showTitle}
            colors={SERIES_COLORS}
            icons={SERIES_ICONS}
            seriesTypes={seriesTypes}
            baselineOnly={baselineOnly}
            height={chartContainerHeight}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'column',
  },
  controlsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: CONTROLS_HEIGHT,
    marginBottom: theme.spacing.small,
  },
  controlsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.small,
  },
  chartContainer: {
    flex: 1,
    position: 'relative',
  },
  chartControlsOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.small,
    height: CONTROLS_HEIGHT,
  },
  errorContainer: {
    flex: 1,
    paddingTop: CONTROLS_HEIGHT + theme.spacing.xlarge,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorBox: {
    width: '100%',
  },
});
